import type { FileAsset } from "@nlc/shared-types";

import { Download } from "lucide-react";
import { useRef } from "react";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";
import { Checkbox } from "@/components/checkbox";
import { FILE_ICONS } from "@/features/my-files/assets/file-icons";
import { useDownloadFile } from "@/features/my-files/hooks/useDownloadFile";
import { getExtensionFromMime } from "@/utils/getExtensionFromMime.ts";
import { convertUTCtoEastern } from "@/utils/timezoneConverter.ts";

type DisplayAssetProps = {
  attachment: FileAsset;
  handleChange: (attachmentId: number, isSelected: boolean) => void;
  isSelected: boolean;
  onAssetClick?: (assetId: string) => void;
};

export function DisplayOnlyAsset({
  attachment,
  handleChange,
  isSelected = false,
  onAssetClick,
}: DisplayAssetProps) {
  const checkboxRef = useRef<HTMLInputElement>(null);
  const { mutate: downloadFile } = useDownloadFile();

  const handleClick = (e: React.MouseEvent) => {
    // If clicking on the checkbox or buttons, don't open the modal
    if (
      e.target instanceof HTMLElement &&
      (e.target.closest("button") || e.target.closest('input[type="checkbox"]'))
    ) {
      return;
    }

    // If onAssetClick is provided, call it with the asset ID
    if (onAssetClick) {
      e.stopPropagation();
      onAssetClick(attachment.id);
    } else {
      // Otherwise, just click the checkbox
      checkboxRef.current?.click();
    }
  };

  return (
    <div
      className={twMerge(
        "flex items-center justify-between gap-xs rounded-xl border border-border-secondary bg-bg-primary px-xl py-md",
        isSelected
          ? "border-border-brand bg-bg-brand-primary"
          : "hover:bg-bg-primary-hover hover:border-button-secondary-border-hover",
        onAssetClick && "cursor-pointer",
      )}
      onClick={handleClick}
    >
      <div className="flex items-center gap-md overflow-hidden">
        <Checkbox
          className="text-text-secondary"
          inputRef={checkboxRef}
          onChange={(e) => handleChange(attachment.attachmentId, e)}
        />

        <FileIcon attachment={attachment} />
        <div className="flex flex-col overflow-hidden">
          <span
            className="truncate text-sm font-medium text-text-secondary"
            title={attachment?.file?.name}
          >
            {attachment?.file?.name}
          </span>
          {attachment.createdAt && (
            <span className="text-xs text-text-tertiary">
              Added {convertUTCtoEastern(attachment.createdAt, "MMM D, YYYY")}
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 gap-xs">
        <Button
          color="gray"
          isIconOnly
          onPress={() => {
            if (attachment.attachmentId) {
              downloadFile(attachment.attachmentId);
            } else if (attachment.awsDownloadUrl) {
              window.open(attachment.awsDownloadUrl, "_blank");
            }
          }}
          size="sm"
          variant="tertiary"
        >
          <Download className="text-button-tertiary-color-fg" />
        </Button>
      </div>
    </div>
  );
}

export function FileIcon({ attachment }: { attachment: FileAsset }) {
  const extension = getExtensionFromMime(attachment?.mime || "");

  if (extension && extension in FILE_ICONS) {
    const IconComponent = FILE_ICONS[extension as keyof typeof FILE_ICONS];

    return <IconComponent className="size-xl shrink-0" />;
  } else {
    const IconComponent = FILE_ICONS.fallback;

    return <IconComponent className="size-xl shrink-0" />;
  }
}
